import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminMessageApi, learnerMessageApi } from '../api/messageApi'
import type { SendMessageRequest } from '../types/message'

// ===== Query Keys =====
export const messageKeys = {
  all: ['messages'] as const,
  adminList: (page: number) => [...messageKeys.all, 'admin', 'list', page] as const,
  adminDetail: (id: number) => [...messageKeys.all, 'admin', 'detail', id] as const,
  adminStats: (id: number) => [...messageKeys.all, 'admin', 'stats', id] as const,
  learnerList: (page: number) => [...messageKeys.all, 'learner', 'list', page] as const,
  unreadCount: () => [...messageKeys.all, 'unreadCount'] as const,
}

// ===== Admin Hooks =====

export function useAdminMessages(page = 0) {
  return useQuery({
    queryKey: messageKeys.adminList(page),
    queryFn: () => adminMessageApi.getMessageList(page),
  })
}

export function useAdminMessageDetail(messageId: number) {
  return useQuery({
    queryKey: messageKeys.adminDetail(messageId),
    queryFn: () => adminMessageApi.getMessageDetail(messageId),
    enabled: !!messageId,
  })
}

export function useAdminMessageStats(messageId: number) {
  return useQuery({
    queryKey: messageKeys.adminStats(messageId),
    queryFn: () => adminMessageApi.getMessageStats(messageId),
    enabled: !!messageId,
  })
}

export function useSendMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: SendMessageRequest) => adminMessageApi.sendMessage(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.all })
    },
  })
}

// ===== Learner Hooks =====

export function useLearnerMessages(page = 0) {
  return useQuery({
    queryKey: messageKeys.learnerList(page),
    queryFn: () => learnerMessageApi.getMessages(page),
  })
}

export function useLearnerMessageDetail(messageId: number) {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: [...messageKeys.all, 'learner', 'detail', messageId],
    queryFn: async () => {
      const result = await learnerMessageApi.getMessageDetail(messageId)
      // 읽음 처리 후 캐시 무효화 (비동기로 실행)
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: messageKeys.unreadCount() })
        queryClient.invalidateQueries({ queryKey: messageKeys.learnerList(0) })
      }, 100)
      return result
    },
    enabled: !!messageId,
  })
}

export function useUnreadCount() {
  return useQuery({
    queryKey: messageKeys.unreadCount(),
    queryFn: () => learnerMessageApi.getUnreadCount(),
    refetchInterval: 60000, // 1분마다 폴링 (SSE 백업)
  })
}
